/*eslint max-lines-per-function: ["error", 220]*/
import React, { useState, useCallback } from 'react';

import {
  CreateUpdateSurveyAnswerDto,
  GetSurveyForClientDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import {
  QuestionResponseTypeEnum,
  YesNoEnum,
} from '../../../common/pokCore/validation/enums';
import { useLocalTranslation } from '../../../common/hooks/useLocalTranslation';

import { PublicSurveyLanding } from './PublicSurveyLanding';
import { PublicSurveyComplete } from './PublicSurveyComplete';
import { PublicSurveyQuestionsView } from './PublicSurveyQuestionsView';

export interface PublicSurveyState {
  currentQuestionIndex: number;
  answers: CreateUpdateSurveyAnswerDto[];
  isCompleted: boolean;
  hasStarted: boolean;
  isBlocked: boolean;
}

export interface AnswerValues {
  textValue?: string;
  booleanValue?: boolean;
}

interface PublicSurveyContainerProps {
  survey: GetSurveyForClientDto;
  logo?: string;
  currentLanguage: string;
  isPreview?: boolean;
  onCompleted?: (
    answers: CreateUpdateSurveyAnswerDto[],
    surveyId: string,
  ) => Promise<boolean>;
}

export const PublicSurveyContainer = ({
  survey,
  logo,
  currentLanguage,
  isPreview = false,
  onCompleted,
}: PublicSurveyContainerProps) => {
  const [state, setState] = useState<PublicSurveyState>({
    currentQuestionIndex: 0,
    answers: [],
    isCompleted: false,
    hasStarted: false,
    isBlocked: false,
  });
  const [error, setError] = useState<string>('');
  const [direction, setDirection] = useState<'left' | 'right'>('right');

  const { t, tk } = useLocalTranslation('administrationLng', currentLanguage);

  const currentQuestion = survey.questions[state.currentQuestionIndex];

  const getCurrentAnswer: () => AnswerValues = useCallback(() => {
    const answer = state.answers.find(a => a.questionId === currentQuestion.id);
    if (!answer) {
      return {};
    }

    return {
      textValue: answer.textValue,
      booleanValue: answer.booleanValue,
    };
  }, [state.answers, currentQuestion]);

  const handleStart = () => {
    setState(prev => ({ ...prev, hasStarted: true }));
  };

  const handleAnswerChange = (value: AnswerValues) => {
    setError('');

    setState(prev => {
      const existingIndex = prev.answers.findIndex(
        a => a.questionId === currentQuestion.id,
      );

      const newAnswer: CreateUpdateSurveyAnswerDto = {
        questionId: currentQuestion.id,
      } as CreateUpdateSurveyAnswerDto;

      switch (currentQuestion.responseType) {
        case QuestionResponseTypeEnum.Text:
          newAnswer.textValue = value.textValue;
          break;

        case QuestionResponseTypeEnum.YesNo:
          newAnswer.booleanValue = value.booleanValue;
          break;

        case QuestionResponseTypeEnum.YesNoText: {
          newAnswer.booleanValue = value.booleanValue;
          newAnswer.textValue = value.textValue;
          break;
        }

        default:
          break;
      }

      const newAnswers =
        existingIndex >= 0
          ? prev.answers.map((a, i) => (i === existingIndex ? newAnswer : a))
          : [...prev.answers, newAnswer];

      return { ...prev, answers: newAnswers, isBlocked: false };
    });
  };

  const validateCurrentQuestion = (): boolean => {
    const value = getCurrentAnswer();
    const isYesNo =
      currentQuestion.responseType === QuestionResponseTypeEnum.YesNo;
    const isYesNoText =
      currentQuestion.responseType === QuestionResponseTypeEnum.YesNoText;

    if (!currentQuestion.optional) {
      if (
        currentQuestion.responseType === QuestionResponseTypeEnum.Text &&
        !value.textValue?.trim()
      ) {
        setError(t(tk.publicSurvey.requiredField));
        setState(prev => ({ ...prev, isBlocked: false }));
        return false;
      }
      if ((isYesNo || isYesNoText) && value.booleanValue === undefined) {
        setError(t(tk.publicSurvey.requiredField));
        setState(prev => ({ ...prev, isBlocked: false }));
        return false;
      }
    }

    if (currentQuestion.blockOnWrongAnswer && (isYesNo || isYesNoText)) {
      const expectedYes = currentQuestion.expectedYesNoAnswer === YesNoEnum.Yes;
      const expectedNo = currentQuestion.expectedYesNoAnswer === YesNoEnum.No;

      const isWrongAnswer =
        (value.booleanValue === false && expectedYes) ||
        (value.booleanValue === true && expectedNo);

      if (isWrongAnswer) {
        setError(t(tk.publicSurvey.blockedAnswer));
        setState(prev => ({ ...prev, isBlocked: true }));
        return false;
      }
    }

    setState(prev => ({ ...prev, isBlocked: false }));
    return true;
  };

  const handleFinishBlocked = async () => {
    const saved = onCompleted
      ? await onCompleted(state.answers, survey.id)
      : true;
    if (saved) {
      setState(prev => ({ ...prev, isCompleted: true }));
    }
  };

  const handleNext = async () => {
    if (!validateCurrentQuestion()) {
      return;
    }

    const isLastQuestion =
      state.currentQuestionIndex === survey.questions.length - 1;

    if (isLastQuestion) {
      const saved = onCompleted
        ? await onCompleted(state.answers, survey.id)
        : true;
      if (!saved) {
        return;
      }
      setState(prev => ({ ...prev, isCompleted: true }));
    } else {
      setDirection('right');
      setState(prev => ({
        ...prev,
        currentQuestionIndex: prev.currentQuestionIndex + 1,
      }));
    }
    setError('');
  };

  const handleBack = () => {
    if (state.currentQuestionIndex > 0) {
      setDirection('left');
      setState(prev => ({
        ...prev,
        currentQuestionIndex: prev.currentQuestionIndex - 1,
        isBlocked: false,
      }));
      setError('');
    }
  };

  if (!state.hasStarted) {
    return (
      <PublicSurveyLanding
        survey={survey}
        logo={logo}
        onStart={handleStart}
        currentLanguage={currentLanguage}
        isPreview={isPreview}
      />
    );
  }

  if (state.isCompleted) {
    return (
      <PublicSurveyComplete
        clientName={survey.client?.name || ''}
        logo={logo}
        currentLanguage={currentLanguage}
        isPreview={isPreview}
      />
    );
  }

  return (
    <PublicSurveyQuestionsView
      survey={survey}
      state={state}
      error={error}
      direction={direction}
      currentLanguage={currentLanguage}
      isBlocked={state.isBlocked}
      isPreview={isPreview}
      getCurrentAnswer={getCurrentAnswer}
      handleAnswerChange={handleAnswerChange}
      handleBack={handleBack}
      handleNext={handleNext}
      handleFinishBlocked={handleFinishBlocked}
    />
  );
};
